import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { isJsonObject, parseJson } from 'shared/lib/json';

interface SlackUser {
  id: string;
  email: string;
  name: string;
  image?: string;
  team: string;
  teamName?: string;
}

interface SlackAuthContextType {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  user: SlackUser | null;
  login: () => void;
  logout: () => void;
}

const SlackAuthContext = createContext<SlackAuthContextType | undefined>(undefined);

// Slack OAuth configuration
const SLACK_CLIENT_ID = process.env.REACT_APP_SLACK_CLIENT_ID || '';
const SLACK_WORKSPACE_SUBDOMAIN = 'talostrading';
const REDIRECT_URI = window.location.origin;
const SLACK_SCOPE = 'email openid profile';
const AUTH_RETURN_KEY = 'tb-auth-return';

export const isSafeAuthReturnPath = (path: string | null | undefined): path is string =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.includes('://') && path !== '/';

export const rememberAuthReturnPath = (path = `${window.location.pathname}${window.location.search}`): void => {
  try {
    if (isSafeAuthReturnPath(path)) sessionStorage.setItem(AUTH_RETURN_KEY, path);
    else sessionStorage.removeItem(AUTH_RETURN_KEY);
  } catch {
    /* private mode */
  }
};

export const takeAuthReturnPath = (): string | null => {
  try {
    const path = sessionStorage.getItem(AUTH_RETURN_KEY);
    sessionStorage.removeItem(AUTH_RETURN_KEY);
    return isSafeAuthReturnPath(path) ? path : null;
  } catch {
    return null;
  }
};

const optionalString = (value: unknown): boolean => value === undefined || typeof value === 'string';

const isSlackUser = (value: unknown): value is SlackUser => {
  if (!isJsonObject(value)) return false;
  return (
    typeof value.id === 'string' &&
    typeof value.email === 'string' &&
    typeof value.name === 'string' &&
    optionalString(value.image) &&
    typeof value.team === 'string' &&
    optionalString(value.teamName)
  );
};

const readStoredSlackUser = (): SlackUser | null => {
  try {
    const storedUser = localStorage.getItem('slack_user');
    if (!storedUser) return null;
    const parsed = parseJson(storedUser);
    return isSlackUser(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const isSlackAuthPayload = (
  value: unknown
): value is {
  user: { id: string; email: string; name: string; image_512?: string; image_192?: string };
  team: { id: string; name?: string };
} => {
  if (!isJsonObject(value) || !isJsonObject(value.user) || !isJsonObject(value.team)) return false;
  const { user, team } = value;
  return (
    typeof user.id === 'string' &&
    typeof user.email === 'string' &&
    typeof user.name === 'string' &&
    optionalString(user.image_512) &&
    optionalString(user.image_192) &&
    typeof team.id === 'string' &&
    optionalString(team.name)
  );
};

interface SlackAuthProviderProps {
  children: ReactNode;
}

export function SlackAuthProvider({ children }: SlackAuthProviderProps) {
  const [user, setUser] = useState<SlackUser | null>(() => readStoredSlackUser());
  const [isAuthenticated, setIsAuthenticated] = useState(() => localStorage.getItem('slack_user') != null);
  const [isAuthenticating, setIsAuthenticating] = useState(() =>
    new URLSearchParams(window.location.search).has('code')
  );

  const handleOAuthCallback = useCallback(
    async (code: string) => {
      try {
        setIsAuthenticating(true);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);

        // Pass the same redirect_uri used in the authorize step. Same-origin GET
        // fetch often omits Origin, and process.env.URL is the production domain.
        const redirectUri = window.location.origin;
        const response = await fetch(
          `/.netlify/functions/slack-auth?code=${encodeURIComponent(code)}&redirect_uri=${encodeURIComponent(redirectUri)}`
        );

        if (response.ok) {
          const data = parseJson(await response.text());
          if (!isSlackAuthPayload(data)) throw new Error('Invalid Slack auth payload');

          const slackUser: SlackUser = {
            id: data.user.id,
            email: data.user.email,
            name: data.user.name,
            image: data.user.image_512 || data.user.image_192,
            team: data.team.id,
            teamName: data.team.name,
          };

          localStorage.setItem('slack_user', JSON.stringify(slackUser));
          setUser(slackUser);
          setIsAuthenticated(true);
          const returnTo = takeAuthReturnPath();
          if (returnTo && returnTo !== `${window.location.pathname}${window.location.search}`) {
            window.location.replace(returnTo);
            return;
          }
        } else {
          const errorBody = parseJson(await response.text().catch(() => '{}'));
          const errorMessage =
            isJsonObject(errorBody) && typeof errorBody.details === 'string'
              ? errorBody.details
              : isJsonObject(errorBody) && typeof errorBody.error === 'string'
                ? errorBody.error
                : 'Please try again';
          console.error('Auth failed:', response.status, errorBody);
          alert(`Authentication failed: ${errorMessage}\n\nCheck Netlify function logs for more details.`);
        }
        setIsAuthenticating(false);
      } catch (error) {
        console.error('Error handling OAuth callback:', error);
        alert('Authentication error. Check browser console and Netlify logs for details.');
        setIsAuthenticating(false);
      }
    },
    [setIsAuthenticating]
  );

  // Handle OAuth callback; session is already hydrated synchronously from localStorage.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) handleOAuthCallback(code);
  }, [handleOAuthCallback]);

  const login = useCallback(() => {
    rememberAuthReturnPath();
    // Use workspace subdomain in authorization URL to skip workspace selection screen
    const slackAuthUrl = `https://${SLACK_WORKSPACE_SUBDOMAIN}.slack.com/openid/connect/authorize?response_type=code&client_id=${SLACK_CLIENT_ID}&redirect_uri=${encodeURIComponent(
      REDIRECT_URI
    )}&scope=${encodeURIComponent(SLACK_SCOPE)}`;

    window.location.href = slackAuthUrl;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('slack_user');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      isAuthenticating,
      user,
      login,
      logout,
    }),
    [isAuthenticated, isAuthenticating, user, login, logout]
  );

  return <SlackAuthContext.Provider value={contextValue}>{children}</SlackAuthContext.Provider>;
}

export function useSlackAuth() {
  const context = useContext(SlackAuthContext);
  if (context === undefined) {
    throw new Error('useSlackAuth must be used within a SlackAuthProvider');
  }
  return context;
}
